import { useState, useEffect, useCallback } from 'react';

import { isExternalLink } from 'src/routes/utils';
import { usePathname, useSearchParams } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';

import { NavItem } from './nav-item';
import { navSectionClasses } from '../classes';
import { NavUl, NavLi, NavCollapse } from '../styles';
import { hasActiveNavChild, findActiveNavItemPath } from '../tree-utils';

// ----------------------------------------------------------------------

export function NavList({
  data,
  render,
  depth,
  slotProps,
  selected,
  openMenu,
  onToggleMenu,
  enabledRootRedirect,
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const selfActive = useActiveLink(data.path, data.deepMatch === true);
  const descendantActive = hasActiveNavChild(pathname, searchParams, data.children);
  const active = selected ?? (data.children ? descendantActive : selfActive);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggleMenu(data.path);
    }
  }, [data.children, data.path, onToggleMenu]);

  const renderNavItem = (
    <NavItem
      render={render}
      // slots
      path={data.path}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      depth={depth}
      active={active}
      disabled={data.disabled}
      hasChild={!!data.children}
      open={data.children && openMenu}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onClick={handleToggleMenu}
    />
  );

  // Hidden item by role
  if (data.roles && slotProps?.currentRole) {
    if (!data?.roles?.includes(slotProps?.currentRole)) {
      return null;
    }
  }

  // Has children
  if (data.children) {
    return (
      <NavLi
        disabled={data.disabled}
        sx={{
          [`& .${navSectionClasses.li}`]: {
            '&:first-of-type': { mt: 'var(--nav-item-gap)' },
          },
        }}
      >
        {renderNavItem}

        <NavCollapse data-group={data.title} in={openMenu} depth={depth} unmountOnExit mountOnEnter>
          <NavSubList
            data={data.children}
            render={render}
            depth={depth}
            slotProps={slotProps}
            enabledRootRedirect={enabledRootRedirect}
          />
        </NavCollapse>
      </NavLi>
    );
  }

  // Default
  return <NavLi disabled={data.disabled}>{renderNavItem}</NavLi>;
}

// ----------------------------------------------------------------------

function NavSubList({ data, render, depth, slotProps, enabledRootRedirect }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const activeChildPath = findActiveNavItemPath(pathname, searchParams, data);
  const activeChildHasChildren = data.find((item) => item.path === activeChildPath)?.children;

  const [openChildPath, setOpenChildPath] = useState(
    activeChildHasChildren ? activeChildPath : null
  );

  useEffect(() => {
    setOpenChildPath(activeChildHasChildren ? activeChildPath : null);
  }, [activeChildHasChildren, activeChildPath]);

  const handleToggleChild = useCallback((path) => {
    setOpenChildPath((currentPath) => (currentPath === path ? null : path));
  }, []);

  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          selected={activeChildPath === list.path}
          openMenu={openChildPath === list.path}
          onToggleMenu={handleToggleChild}
        />
      ))}
    </NavUl>
  );
}
